import { FC, useState } from 'react'
import {
  Grid,
  Typography,
  ListItem,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import DeleteIcon from '@mui/icons-material/Delete'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'

import { EditItem } from './EditItem'
import { DeleteItem } from './DeleteItem'

import { SlipsScreenType, TabsEnums, InlineEditableField } from '../../../components'
import { ItemEntry, EntryType, ItemEntryDetail, WEIGHT_CHARGE_ITEM_TYPE, isLostEntry } from '../../../services'
import { getSlipsLabels } from './slipsLabels'
import './slips_style.css'
import { AddEditPaymentItem } from './AddEditPayment'
import { DeletePayment } from './DeletePayment'
import {
  currencyFormatter,
  formatSlipRate,
  getShortFormattedDate,
  getTimeOnly,
} from '../../../utils'
import { Box } from '@mui/system'

const ItemName: FC<{ name: string; time: string; id?: string; itemId?: number }> = ({
  name,
  time,
  id,
  itemId
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 3 }}>
      {/* ✅ First Row: Name + (Item Code) */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          data-cy={`${id}_name`}
          variant="body2"
          id={id}
          sx={{
            py: 0,
            m: 1.2,
          }}
        >
          {name}
        </Typography>
        {itemId && itemId > 0 && (
          <Typography
           fontSize={10}
            sx={{
              color: '#666',
              ml: 0,
            }}
            data-cy={`${id}_item_code`}
          >
            (#{itemId})
          </Typography>
        )}

        
      </Box>

      {/* ✅ Second Row: Time (if present) */}
      {time && (
        <Typography
          data-cy={`${id}_time_type`}
          variant="body2"
          sx={{
            fontSize: 11,
            color: 'crimson',
            ml:1.2,
            mt: -0.5, // slight spacing adjustment
          }}
        >
          {time}
        </Typography>
      )}
    </Box>
  )
}

interface ListItemComponentProps {
  item: ItemEntry
  componentName: TabsEnums
  billId: number
  screenType: SlipsScreenType
  challan: ItemEntryDetail
  accountType?: string
  onItemDoubleClick?: (itemName: string) => void
  onSlipsEntriesUpdated?: () => void
}

export const ListItemComponent: FC<ListItemComponentProps> = ({
  item,
  componentName,
  billId,
  screenType,
  challan,
  accountType,
  onItemDoubleClick,
  onSlipsEntriesUpdated,
}) => {
  const theme = useTheme()
  const isExcess = item.secondaryType === 'EXCESS_QUANTITY'
  const logicalTag = isExcess
    ? 'EXCS'
    : item.entryType === EntryType.IN && item.receivedAmount > 0
    ? 'CRDT'
    : item.entryType === EntryType.IN && item.receivedAmount < 0
    ? 'RFND'
    : item.entryType === EntryType.OUT && item.isConsumable
    ? 'OTHR'
    : item.entryType === EntryType.OUT
    ? 'ISSU'
    : item.entryType === EntryType.IN && isLostEntry(item)
    ? 'LOST'
    : item.entryType === EntryType.IN
    ? 'RECV'
    : ' '
  const labels = getSlipsLabels(accountType)
  const issuedRecievedText =
    logicalTag === 'EXCS'
      ? 'EXCS'
      : logicalTag === 'RECV'
      ? labels.entryTagRecv
      : logicalTag === 'ISSU'
      ? labels.entryTagIssu
      : logicalTag

  // submission_type={'LOST'}

  const [editItem, openEditItem] = useState<boolean>(false)
  const [deleteItem, openDeleteItem] = useState<boolean>(false)
  const addPaymentRequest = {
    billId: billId,
    paymentId: item.paymentId,
    amount: item.receivedAmount,
    paymentDate: item.entryDate,
    note: item.note,
    tdsAmount: item.tdsAmount ?? 0,
  }

  const showTimeType =
    (item.entryType === 'IN' && item.timeType === 'MORNING') ||
    (item.entryType === 'OUT' && item.timeType === 'EVENING')
  const slipDate = getShortFormattedDate(new Date(item.entryDate))
  const testIdPrefix = `id_typo_${slipDate}_${item.itemName}`

  return (
    <ListItem
      id="slip_row"
      sx={{
        pt: 0.5,
        pb: 0.5,
      }}
      key={item.id}
      onDoubleClick={(e) => {
        if (!onItemDoubleClick) return
        e.preventDefault()
        onItemDoubleClick(item.itemName)
      }}
    >
      <Grid container sx={{ p: 0 }} alignItems="center" justifyContent="center">
        <Grid item xs={3} sx={{ alignItems: 'center' }}>
          <div
            style={{
              height: 10,
              width: 10,
              display: 'flex',
              borderRadius: 5,
              backgroundColor: item.barColorCode,
              marginLeft: 20,
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
          <ItemName
            name={item.itemName}
            time={showTimeType ? item.timeType : ''}
            id={testIdPrefix}
            itemId={item.id}
          />
        </Grid>

        <Grid item xs={2} sx={{ alignItems: 'center' }}>
          <Typography data-cy={`${testIdPrefix}_time`}>
            {item.id >= 0 ? getTimeOnly(item.entryDate) : ''}
          </Typography>
        </Grid>

        {(componentName === TabsEnums.All ||
          componentName === TabsEnums.Out ||
          componentName === TabsEnums.In) && (
          <>
            <Grid item xs={2} sx={{ alignItems: 'center'}}>
              {item.entryType === EntryType.OUT &&
                item.id > 0 &&
                (item.rate || item.rate === 0) &&
                (!item.cycleRates || item.cycleRates.trim() === '') && (
                  <Box sx={{ m: 0.5 }}>
                    <InlineEditableField
                      value={item.rate}
                      fieldName="rate"
                      entryId={item.id}
                      entity="entry"
                      formatter={formatSlipRate}
                      parser={(val) => parseFloat(val.replace(/[^0-9.]/g, ''))}
                      type="number"
                      min={0}
                      dataCy={`${testIdPrefix}_rate`}
                      onUpdateSuccess={onSlipsEntriesUpdated}
                    />
                  </Box>
                )}
              {item.entryType === EntryType.OUT &&
                item.id > 0 &&
                item.cycleRates &&
                item.cycleRates.trim() !== '' && (
                  <Typography
                    variant="body2"
                    sx={{ m: 0.5, textAlign:'right', pr:3 }}
                    data-cy={`${testIdPrefix}_cycle_rates`}
                  >
                    {item.cycleRates}
                  </Typography>
                )}

              {item.receivedAmount !== 0 && (
                <Box sx={{ m: 0.5 }}>
                  <InlineEditableField
                    isNegative = {item.receivedAmount < 0}
                    value={Math.abs(item.receivedAmount)}
                    fieldName="receivedAmount"
                    entryId={item.paymentId}
                    entity="payment"
                    formatter={currencyFormatter.format}
                    parser={(val) => parseFloat(val.replace(/[^0-9.]/g, ''))}
                    type="number"
                    min={0}
                    dataCy={`${testIdPrefix}_rate`}
                    onUpdateSuccess={onSlipsEntriesUpdated}
                  />
                </Box>
              )}
              {isLostEntry(item) && (
                <Box sx={{ m: 0.5 }}>
                  <InlineEditableField
                    value={item.lostItemCost}
                    fieldName="lostItemCost"
                    entryId={item.id}
                    entity="entry"
                    formatter={currencyFormatter.format}
                    parser={(val) => parseFloat(val.replace(/[^0-9.]/g, ''))}
                    type="number"
                    min={0}
                    dataCy={`${testIdPrefix}_lost_item_cost`}
                    onUpdateSuccess={onSlipsEntriesUpdated}
                  />
                </Box>
              )}
            </Grid>

            <Grid
              item
              xs={2}
              sx={{ alignItems: 'center', alignContent: 'center' }}
            >
              {(item?.itemType == WEIGHT_CHARGE_ITEM_TYPE ||  item?.isConsumable !== true) && item.paymentId == 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: 70, ml:3 }}>
                  <InlineEditableField
                    value={item.quantity}
                    fieldName="quantity"
                    entryId={item.id}
                    entity="entry"
                    type="number"
                    min={0}
                    dataCy={`${testIdPrefix}_quantity`}
                    onUpdateSuccess={onSlipsEntriesUpdated}
                  />
                </Box>
              ) : (
                <Typography
                  id="item_quantity"
                  variant="body2"
                  textAlign={'right'}
                  sx={{
                    width: 70,
                  }}
                  data-cy={`${testIdPrefix}_quantity`}
                >
                  {item.paymentId !== 0 && item.tdsAmount != null && item.tdsAmount > 0
                    ? `tds(${currencyFormatter.format(item.tdsAmount)})`
                    : '-'}
                </Typography>
              )}
            </Grid>

            <Grid
              id="issued_recieved_text"
              item
              xs={2}
              sx={{ alignItems: 'center' }}
            >
              <div
                className={logicalTag === 'EXCS' ? 'entryTypeEXCS' : `entryType${logicalTag}`}
                data-cy={`${testIdPrefix}_entry_type`}
                style={
                  item.barColorCode
                    ? {
                        backgroundColor: alpha(item.barColorCode, 0.25),
                        color: item.barColorCode,
                      }
                    : undefined
                }
              >
                {issuedRecievedText}
              </div>
            </Grid>

            <Grid item xs={1}>
              {(screenType === SlipsScreenType.Account && (item.itemId > 0 || item.paymentId > 0)) && (
                <>
                  <Tooltip title="Edit" arrow>
                    <IconButton
                      id="edit_entry"
                      data-cy={`${testIdPrefix}_button_edit`}
                      sx={{
                        '&:hover': {
                          background: theme.colors.primary.lighter,
                        },
                      }}
                      color="inherit"
                      onClick={() => {
                        openEditItem(true)
                      }}
                    >
                      <EditCalendarIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton
                      id="delete_entry"
                      data-cy={`${testIdPrefix}_button_delete`}
                      sx={{
                        '&:hover': { background: theme.colors.error.lighter },
                        color: 'crimson',
                      }}
                      color="inherit"
                      onClick={() => {
                        openDeleteItem(true)
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Grid>
          </>
        )}
      </Grid>
      {editItem && item.paymentId > 0 && (
        <AddEditPaymentItem
          item={addPaymentRequest}
          accountType={accountType}
          onClose={() => {
            openEditItem(false)
          }}
          onSubmitSuccess={() => {
            openEditItem(false)
          }}
        />
      )}
      {editItem && item.paymentId == 0 && (
        <EditItem
         challanType={challan.challanType}
          entryType={item.entryType}
          entryId={item.id}
          siteId={item.billId}
          challanNo={item.challanNo}
          onClose={() => {
            openEditItem(false)
          }}
          onSubmitSuccess={() => {
            openEditItem(false)
          }}
        />
      )}
      {deleteItem && item.paymentId > 0 && (
        <DeletePayment
          item={item}
          onClose={() => {
            openDeleteItem(false)
          }}
          onSubmitSuccess={() => {
            openDeleteItem(false)
          }}
        />
      )}
      {deleteItem && item.paymentId == 0 && (
        <DeleteItem
          item={item}
          onClose={() => {
            openDeleteItem(false)
          }}
          onSubmitSuccess={() => {
            openDeleteItem(false)
          }}
        />
      )}
    </ListItem>
  )
}
